import { useState } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { getCardInfo, getCardStats, getRecentPurchases } from '@/api/card';
import { getCardFamily, getRelatedCards } from '@/api/products';
import { updateCardWatches } from '@/api/watches';
import { getMyProfile } from '@/api/profile';
import useAuthStore from '@/stores/authStore';
import { useToast } from '@/components/ToastProvider';
import { useTranslation } from 'react-i18next';
import Breadcrumb from '@/components/Breadcrumb';
import CardGallerySection from '@/sections/card/CardGallerySection';
import { redirectToLogin } from '@/utils/authUtils';
import CardBuyInfoSection from '@/sections/card/CardBuyInfoSection';
import RecentPurchasesSection from '@/sections/card/RecentPurchasesSection';
import CardParametersSection from '@/sections/card/CardParametersSection';
import PriceChartSection from '@/sections/card/PriceChartSection';
import RelatedCardsSection from '@/sections/card/RelatedCardsSection';
import FaqSection from '@/sections/card/FaqSection';
import { TooltipProvider } from '@/components/Tooltip';
import BindPhoneDialog from '@/components/BindPhoneDialog';
import { formatCurrency } from '@/utils/formatUtils';
import { getCards } from '@/api/products';
import PenaltyRestrictionDialog from '@/components/PenaltyRestrictionDialog';
import usePenaltyGuard from '@/hooks/usePenaltyGuard';

const CardDetailPageContent = () => {
  const { t } = useTranslation();
  const { id } = useParams();
  const { user } = useAuthStore();
  const navigate = useNavigate();
  const location = useLocation();
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const [isPhoneDialogOpen, setIsPhoneDialogOpen] = useState(false);
  const [phoneDialogCallback, setPhoneDialogCallback] = useState(null);
  const searchParams = new URLSearchParams(location.search);
  const cameFromBrandPage = searchParams.get('origin') === 'brand';

  // Get authentication state
  const accessToken = useAuthStore((state) => state.accessToken);
  const { ensureActionAllowed, penaltyDialogProps } = usePenaltyGuard();

  const { data: cardInfo, isLoading: isLoadingCard } = useQuery({
    queryKey: ['card', id],
    queryFn: () => getCardInfo(id),
  });

  // Only load profile when user is logged in
  const { data: userProfile, isLoading: isLoadingProfile } = useQuery({
    queryKey: ['user-profile'],
    queryFn: getMyProfile,
    enabled: !!accessToken, // Only fetch when user is authenticated
  });

  const { data: cardStats, isLoading: isLoadingStats } = useQuery({
    queryKey: ['cardStats', id],
    queryFn: () => getCardStats(id),
    refetchInterval: 5 * 60 * 1000, // 5 minutes in milliseconds
  });
  
  // Get card type for determining API calls
  const cardType = cardInfo?.data?.type || 'SINGLE_CARD';
  
  // Determine listingType for the API based on the card type
  const listingType = cardType === 'SINGLE_CARD' ? 'PSA_SINGLE_CARD' : 'CARD_BOX';
  
  // Fetch recent purchases
  const { data: recentPurchasesData, isLoading: isLoadingPurchases } = useQuery({
    queryKey: ['recentPurchases', id, listingType],
    queryFn: () => getRecentPurchases(id, listingType),
    enabled: !!id && !!listingType,
  });

  const { data: varietiesData, isLoading: isLoadingVarieties } = useQuery({
    queryKey: ['cardFamily', id],
    queryFn: () => getCardFamily(id, false),
    enabled: !!id,
  });
  
  // Fetch related cards using the new API
  const { data: relatedCardsData, isLoading: isLoadingRelatedCards } = useQuery({
    queryKey: ['relatedCards', id],
    queryFn: () => getRelatedCards(id),
    enabled: !!id,
  });

  // Fetch box cards related to the current card's set
  // Only enabled when current card type is SINGLE_CARD
  const {
    data: boxCards,
    isLoading: isBoxLoading,
  } = useQuery({
    queryKey: ['boxCards', cardInfo?.data?.set?.code],
    queryFn: () =>
      getCards({
        setCode: cardInfo?.data?.set?.code,
        type: 'CARD_BOX',
        sort: 'POPULARITY',
        direction: 'DESC',
        limit: 20,
      }),
    enabled: !!id && cardInfo?.data?.type === 'SINGLE_CARD' && !!cardInfo?.data?.set?.code,
  });

  // Add or update watch mutation
  const updateWatchMutation = useMutation({
    mutationFn: ({ type, options }) => {
      return updateCardWatches(id, type, options);
    },
    onSuccess: () => {
      queryClient.invalidateQueries(['card', id]);
    },
    onError: (error) => {
      toast(`Error: ${error.message}`);
    }
  });

  const handleUpdateWatch = (selectedIds) => {
    // Check if user is logged in first
    if (!accessToken) {
      redirectToLogin(navigate, location);
      return;
    }

    // For single cards, convert the lowercase IDs back to PSA format
    const options = cardType === 'SINGLE_CARD'
      ? selectedIds.map(id => id.toUpperCase().replace('PSA', 'PSA_'))
      : selectedIds.map(id => parseInt(id));

    updateWatchMutation.mutate({
      type: cardType === 'SINGLE_CARD' ? 'PSA_SINGLE_CARD' : 'CARD_BOX',
      options
    });
  };

  const handleAttemptPurchase = ({ selectedGrade, selectedBoxQuantity,isOfficial }) => {
    // Check if user is logged in first
    if (!accessToken) {
      redirectToLogin(navigate, location);
      return;
    }

    if (!userProfile?.data?.phone) {
      setPhoneDialogCallback(() => () => handleAttemptPurchase({ selectedGrade, selectedBoxQuantity }));
      setIsPhoneDialogOpen(true);
      return;
    }

    let psaGradeToPurchase;
    let conditionToPurchase; // For future use if non-PSA direct purchase is added
    let bundledQuantityToPurchase;
    let priceToPurchase;

    if (cardType === 'SINGLE_CARD') {
      const stat = cardStats?.data?.psaStats?.find(s => s.psa.toLowerCase() === selectedGrade);
      if (stat) {
        psaGradeToPurchase = stat.psa; // API format e.g. PSA_10
        priceToPurchase = isOfficial ? stat.lowestPriceOnSaleOfficial : stat.lowestPriceOnSale;
      } else {
        // Handle case where selected grade might not be found (e.g. data sync issue)
        toast(t('error.selected_grade_unavailable', 'Selected grade is no longer available.'));
        return;
      }
    } else { // CARD_BOX
      const stat = cardStats?.data?.bundledQuantityStats?.find(s => s.quantity === selectedBoxQuantity);
      if (stat) {
        bundledQuantityToPurchase = stat.quantity;
        priceToPurchase = isOfficial ? stat.lowestPriceOnSaleOfficial : stat.lowestPriceOnSale;
      } else {
        // Handle case where selected quantity might not be found
        toast(t('error.selected_quantity_unavailable', 'Selected quantity is no longer available.'));
        return;
      }
    }

    if (priceToPurchase === undefined || priceToPurchase === null) {
      // This case should ideally be prevented by disabling sold-out options in CardBuyInfoSection
      toast(t('error.item_sold_out', 'This item is currently sold out.'));
      return;
    }

    const params = new URLSearchParams();
    if (psaGradeToPurchase) params.set('psa', psaGradeToPurchase);
    if (conditionToPurchase) params.set('condition', conditionToPurchase);
    if (bundledQuantityToPurchase) params.set('bundledQuantity', bundledQuantityToPurchase.toString());
    if (priceToPurchase !== undefined) params.set('price', priceToPurchase.toString());
    if (isOfficial) params.set('isOfficial', 'true');

    if (!ensureActionAllowed()) {
      return;
    }

    navigate(`/card/${id}/purchase?${params.toString()}`);
  };

  const handleAttemptList = () => {
    // Check if user is logged in first
    if (!accessToken) {
      redirectToLogin(navigate, location);
      return;
    }

    // Check if user has a verified phone
    if (!userProfile?.data?.phone) {
      setPhoneDialogCallback(() => () => handleAttemptList());
      setIsPhoneDialogOpen(true);
      return;
    }

    if (!ensureActionAllowed()) {
      return;
    }

    // Navigate to sell page
    navigate(`/card/${id}/sell`);
  };

  const handleListingPurchase = (listing) => {
    // Check if user is logged in first
    if (!accessToken) {
      redirectToLogin(navigate, location);
      return;
    }

    // Check if user has a verified phone
    if (!userProfile?.data?.phone) {
      setPhoneDialogCallback(() => () => handleListingPurchase(listing));
      setIsPhoneDialogOpen(true);
      return;
    }

    if (!ensureActionAllowed()) {
      return;
    }

    // Navigate to purchase page with listing details
    navigate(`/card/${id}/purchase/?psa_listing_id=${listing.id}&psa=${listing.psa || listing.product?.psa}&price=${listing.price}&isOfficial=${listing.isOfficial}`);
  };

  const handlePhoneDialogClose = (success) => {
    setIsPhoneDialogOpen(false);
    queryClient.invalidateQueries(['user-profile']);
    
    // If binding was successful and we have a callback, execute it
    if (success && phoneDialogCallback) {
      phoneDialogCallback();
    }
    setPhoneDialogCallback(null);
  };

  // Only show loading for profile if user is authenticated
  const shouldWaitForProfile = accessToken && isLoadingProfile;

  if (isLoadingCard || isLoadingStats || shouldWaitForProfile) {
    return (
      <div className='container mx-auto flex justify-center items-center'>
        <span>{t('common.loading')}</span>
      </div>
    );
  }

  // Get watch data from card info
  const watchData = cardInfo?.data?.viewerWatchingInfo || null;
  
  // Transform watch data for the UI
  let favoritedItems = [];
  if (watchData) {
    favoritedItems = cardType === 'SINGLE_CARD'
      ? (watchData.psaGrades || []).map(grade => grade.toLowerCase().replace('_', ''))
      : (watchData.bundledQuantities || []).map(qty => qty.toString());
  }

  // Create varieties array from current card data if varieties are still loading
  const varieties = isLoadingVarieties 
      ? cardInfo?.data 
        ? [{
            id: cardInfo.data.id,
            name: cardInfo.data.localizedName,
            rarity: cardInfo.data.rarity,
            imageUrl: cardInfo.data.images?.[0]?.url,
          }] 
        : []
      : varietiesData?.data
        ? varietiesData.data.map(variety => ({
            id: variety.id,
            name: variety.localizedName,
            rarity: variety.rarity,
            imageUrl: variety.images?.[0]?.url,
          }))
        : [];
  
  // Process related cards - no need to filter
  const relatedCards = relatedCardsData?.data || [];

  // Get recent purchases data
  const recentPurchases = recentPurchasesData?.data || [];
  // Transform API data to match the UI requirements
  const transformedData = {
    id: cardInfo?.data?.id,
    name: `${cardInfo?.data?.localizedRawName || cardInfo?.data?.localizedName}${cardType !== 'CARD_BOX' && cardInfo?.data?.rarityInfo?.sub?.name ? ` (${cardInfo.data.rarityInfo.sub.name})` : ''}`,
    desc: cardInfo?.data?.name,
    price: (() => {
      const vals = [
        cardInfo?.data?.stats?.lowestPriceOnSale,
        cardInfo?.data?.stats?.lowestPriceOnSaleOfficial,
      ].filter(v => typeof v === 'number' && v > 0);
      return vals.length ? Math.min(...vals) : 0;
    })(),
    salesVolume: cardStats?.data?.salesVolume,
    canList: !cardInfo?.data?.onlyOfficial || (user && user?.roles?.includes('OFFICIAL')),
    type: cardType,
    parameters: {
      brand: cardInfo?.data?.brand?.localizedName,
      type: cardType,
      code: cardInfo?.data?.type === 'CARD_BOX' ? cardInfo?.data?.set?.code : cardInfo?.data?.code,
      set: cardInfo?.data?.set?.localizedName,
      releaseDate: cardInfo?.data?.releaseDate,
      price: cardInfo?.data?.type === 'CARD_BOX' && cardInfo?.data?.originalPrice ? formatCurrency(cardInfo?.data?.originalPrice) : undefined,
    },
    images: cardInfo?.data?.images?.map(img => img.url),
    recentPurchases,
    relatedCards: relatedCards,
  };

  const brandInfo = cardInfo?.data?.brand;
  const shouldShowBrandBreadcrumb = cameFromBrandPage && brandInfo?.localizedName;

  const middleBreadcrumb = shouldShowBrandBreadcrumb
    ? {
        label: brandInfo.localizedName,
        ...(brandInfo.id ? { href: `/brands/${brandInfo.id}` } : {}),
      }
    : { label: t('home.sections.hot_cards'), href: '/' };

  const breadcrumbItems = [
    { label: t('common.home'), href: '/' },
    middleBreadcrumb,
    { label: transformedData.name },
  ];

  return (
    <div className="container py-8">
      <Breadcrumb items={breadcrumbItems} />
      
      <div className="mt-6 grid grid-cols-5 gap-12">
        {/* Left Column */}
        <div className="col-span-3 relative after:content-[''] after:absolute after:w-px after:bg-[#f4f4f4] after:top-0 after:bottom-0 after:right-[-24px] after:z-[-1]">
          <CardGallerySection 
            images={transformedData.images} 
            varieties={varieties}
            cardType={transformedData.type}
            currentCardId={id}
          />
          <div className="w-full">
            <RecentPurchasesSection 
              purchases={transformedData.recentPurchases} 
              salesVolume={transformedData.salesVolume}
            />
            <CardParametersSection 
              parameters={transformedData.parameters} 
              rarityInfo={cardInfo?.data?.rarityInfo}
            />
            <PriceChartSection 
              cardId={id}
              cardType={transformedData.type} 
              listingType={listingType}
            />
            <RelatedCardsSection cards={transformedData.relatedCards} cardId={id} />
            <FaqSection />
          </div>
        </div>

        <div className="col-span-2">
          {/* Right Column */}
          <CardBuyInfoSection
            name={transformedData.name}
            localizedRawName={cardInfo?.data?.localizedRawName}
            desc={transformedData.desc}
            notes={cardInfo?.data?.notes}
            code={cardInfo?.data?.code}
            rawCode={cardInfo?.data?.rawCode}
            rarityInfo={cardInfo?.data?.rarityInfo}
            set={cardInfo?.data?.set}
            price={transformedData.price}
            canList={transformedData.canList}
            salesVolume={transformedData.salesVolume}
            cardType={transformedData.type}
            boxType={cardInfo?.data?.boxType}
            boxCards={cardInfo?.data?.type === 'SINGLE_CARD' && !isBoxLoading ? boxCards?.data || [] : []}
            psaStats={cardType === 'SINGLE_CARD' ? cardStats?.data?.psaStats || [] : []}
            conditionStats={cardType === 'SINGLE_CARD' ? cardStats?.data?.conditionStats || [] : []}
            bundledQuantityStats={cardType === 'CARD_BOX' ? cardStats?.data?.bundledQuantityStats || [] : []}
            brandInfo={cardInfo?.data?.brand}
            favoritedItems={favoritedItems}
            onUpdateFavorites={handleUpdateWatch}
            onAttemptPurchase={handleAttemptPurchase}
            onAttemptList={handleAttemptList}
            onListingPurchase={handleListingPurchase}
          />
        </div>
      </div>
      {isPhoneDialogOpen && (
        <BindPhoneDialog onClose={handlePhoneDialogClose} />
      )}
      <PenaltyRestrictionDialog {...penaltyDialogProps} />
    </div>
  );
};

// Wrapper with TooltipProvider
const CardDetailPage = () => {
  return (
    <TooltipProvider>
      <CardDetailPageContent />
    </TooltipProvider>
  );
};

export default CardDetailPage; 
